import { ref } from 'vue';
import { resolvePageTitle } from 'shared/helpers/pageTitle';
import { ACCESS_MODULES } from '../routes/dashboard/settings/agents/accessProfilesCatalog';

export const accessProfileNotice = ref(null);
let currentProfileName = '';
let currentAccountId = null;
let currentUserId = null;

const ACTION_LABELS = {
  conversation_view: 'visualizar conversas e suas mensagens',
  conversation_page_access: 'acessar a página de conversas',
  conversation_create: 'iniciar esta conversa',
  conversation_reply: 'responder esta conversa',
  conversation_claim: 'assumir este atendimento',
  conversation_transfer: 'transferir esta conversa',
  conversation_resolve: 'concluir esta conversa',
  conversation_reopen: 'reabrir esta conversa',
  whatsapp_keys_manage: 'gerenciar as chaves de instância do WhatsApp',
  managed_inboxes_manage: 'gerenciar as caixas de entrada das integrações',
};

const MODULE_LABELS = Object.fromEntries(
  ACCESS_MODULES.filter(module => module.permission).map(module => [
    module.permission,
    module.label,
  ])
);
Object.assign(MODULE_LABELS, {
  conversation_manage: 'Conversas',
  conversation_unassigned_manage: 'Conversas sem responsável',
  conversation_participating_manage: 'Conversas em que participa',
  report_manage: 'Relatórios',
  knowledge_base_manage: 'Central de ajuda',
});

const PAGE_LABELS = Object.fromEntries(
  ACCESS_MODULES.flatMap(module =>
    module.pages.map(page => [
      page.permission,
      module.label === page.label
        ? page.label
        : `${module.label} → ${page.label}`,
    ])
  )
);

const ROUTE_LABELS = {
  home: 'Atendimento',
  aceleres_attendances: 'Atendimentos',
  aceleres_team_presence_history: 'Histórico da equipe',
  agent_list: 'Usuários',
  configuracoes_usuarios: 'Usuários',
  configuracoes_novo_usuario: 'Novo usuário',
  configuracoes_modelos_mensagens: 'Modelos de mensagens',
  custom_roles_list: 'Perfis de acesso',
  general_settings_index: 'Configurações gerais',
  security_settings_index: 'Segurança',
  auditlogs_list: 'Registros de auditoria',
  billing_settings_index: 'Faturamento',
  captain_settings_index: 'Configurações do Jarvis',
};

export const accessProfileRouteName = route => {
  if (ROUTE_LABELS[route?.name]) return ROUTE_LABELS[route.name];
  const pageTitle = resolvePageTitle(route).replace(/^Zoraxy - /, '');
  const pagePermission = route?.meta?.pagePermission;
  const permission =
    typeof pagePermission === 'function'
      ? pagePermission(route)
      : pagePermission;
  const pages = Array.isArray(permission) ? permission : [permission];
  return (
    pages.map(page => PAGE_LABELS[page]).find(Boolean) ||
    (pageTitle !== 'Zoraxy' ? pageTitle : null) ||
    route?.meta?.permissions?.map(item => MODULE_LABELS[item]).find(Boolean)
  );
};

export const accessProfileName = (user, accountId) => {
  const account = user?.accounts?.find(
    item => Number(item.id) === Number(accountId)
  );
  return account?.custom_role?.name || account?.access_profile_name || '';
};

export const setAccessProfileContext = (user, accountId) => {
  if (currentAccountId !== Number(accountId) || currentUserId !== user?.id)
    accessProfileNotice.value = null;
  currentAccountId = Number(accountId);
  currentUserId = user?.id;
  currentProfileName = accessProfileName(user, accountId);
};

export const accessProfileMessage = ({
  permission,
  profileName = currentProfileName,
  pageName,
  destinationName,
} = {}) => {
  const profile = profileName
    ? `Seu perfil atual, ${profileName},`
    : 'Seu perfil atual';
  const page =
    pageName || (!ACTION_LABELS[permission] && PAGE_LABELS[permission]);
  let reason;
  if (page) {
    reason = `não inclui acesso à página ${page}`;
  } else if (ACTION_LABELS[permission]) {
    reason = `não permite ${ACTION_LABELS[permission]}`;
  } else if (MODULE_LABELS[permission]) {
    reason = `não inclui acesso ao módulo ${MODULE_LABELS[permission]}`;
  } else {
    reason = 'não inclui a permissão necessária para o acesso solicitado';
  }
  const destination = destinationName
    ? ` Você foi direcionado para a página ${destinationName}, que está disponível para esse perfil.`
    : '';
  return `${profile} ${reason}.${destination}`;
};

export const showAccessProfileNotice = options => {
  // One persistent notice replaces the previous one; simultaneous denials never stack.
  accessProfileNotice.value = {
    message: options?.message || accessProfileMessage(options),
  };
};

export const clearAccessProfileNotice = () => {
  accessProfileNotice.value = null;
};

export const isAccessProfileMessage = message =>
  /(?:^Seu perfil atual[\s\S]* n[ãa]o (?:inclui|permite)|^Voc[êe] est[áa] usando o modo Vendedor, no qual seu usu[áa]rio n[ãa]o tem acesso|Seu usu[áa]rio n[ãa]o tem autoriza[çc][ãa]o|n[ãa]o (?:est[áa]|est[ãa]o) dispon[íi]ve(?:l|is) para o seu perfil atual)/i.test(
    String(message || '')
  );

export const accessProfileResponseMessage = data => {
  const message = data?.message || data?.error;
  if (
    isAccessProfileMessage(message) &&
    /^(?:Seu perfil atual|Você está usando o modo Vendedor)/i.test(message)
  ) {
    return message;
  }
  return accessProfileMessage({
    permission: data?.permission,
    profileName: data?.profile_name,
  });
};
